import { useGetCommonQuery } from '@/entities/system-key'
import { useResetDashboardCacheOnUserChange } from '@/features/dashboard'
import { notificationQueryKeys, useNotificationStream } from '@/features/notification'
import { useRoleMenu, useRoleTabs } from '@/features/role/queries'
import userService from '@/features/user/service'
import { useAuth, useIsLogin } from '@/shared/context'
import { useCrossTabAuthSync } from '@/shared/lib/hooks/use-cross-tab-auth-sync'
import { mapApiMenuToNodes } from '@/shared/lib/menu'
import {
  STORAGE,
  getAuthToken,
  getUserInfo,
  setLocalStorage,
  setUserInfo as setStoredUserInfo,
} from '@/shared/lib/storage'
import {
  getListSystemKey,
  setAppInitialized,
  setListMenu,
  setListTabs,
  setUserInfo,
  useAppDispatch,
} from '@/shared/redux'
import { useQueryClient } from '@tanstack/react-query'
import { type ReactNode, useEffect } from 'react'

interface DefaultActionProps {
  children?: ReactNode
}

const DefaultAction = ({ children }: DefaultActionProps) => {
  const { isLogin } = useIsLogin()
  useCrossTabAuthSync(!!isLogin)

  const { setUser } = useAuth()
  const dispatch = useAppDispatch()
  const queryClient = useQueryClient()
  useResetDashboardCacheOnUserChange()

  const isLoginLocalStorage = getAuthToken()

  const { data: systemKeys, isLoading: isLoadingSystemKeys } = useGetCommonQuery()

  useEffect(() => {
    if (systemKeys) {
      dispatch(getListSystemKey(systemKeys))
    }
  }, [systemKeys, dispatch])

  const { data, isLoading: isLoadingTabs } = useRoleTabs({
    enabled: !!isLoginLocalStorage,
  })
  const {
    data: menuData,
    isLoading: isLoadingMenu,
    isError: isMenuError,
  } = useRoleMenu({
    enabled: !!isLoginLocalStorage,
  })
  useEffect(() => {
    if (menuData?.isOk) dispatch(setListMenu(mapApiMenuToNodes(menuData.object ?? [])))
    else if (isMenuError) dispatch(setListMenu([]))
  }, [dispatch, isMenuError, menuData])
  useEffect(() => {
    if (data?.isOk) {
      dispatch(
        setListTabs([
          {
            categoryId: 0,
            Description: 'Tổng Quan',
            isVisitTab: true,
          },
          {
            categoryId: -1,
            codeKey: 'MyTicket',
            description: 'MyTicket',
            isVisitTab: true,
          },
          ...data.object,
        ])
      )
    }
  }, [data, dispatch])
  useEffect(() => {
    // Auth-related logic (only load if user is logged in)
    if (isLogin) {
      setLocalStorage(
        STORAGE.DEV_MODE,
        !!(typeof window !== 'undefined' && (window as any).env?.API_ROOT)?.IS_DEV
      )

      const userInfo = getUserInfo()
      if (userInfo) {
        dispatch(setUserInfo(userInfo))
        setUser(userInfo)
      }

      // Luôn đồng bộ profile mới nhất từ BE (avatar, tên...) để tránh dữ liệu cũ trong localStorage khi F5
      userService
        .getProfile()
        .then((res) => {
          if (res?.isOk && res.object) {
            const savedInfo = getUserInfo<Record<string, any>>() || {}
            const freshUserInfo = {
              ...savedInfo,
              ...res.object,
            }
            setStoredUserInfo(freshUserInfo)
            dispatch(setUserInfo(freshUserInfo))
            setUser(freshUserInfo as any)
          }
        })
        .catch(() => {})
    }
  }, [dispatch, isLogin, setUser])

  useEffect(() => {
    // App is initialized once GetSystemKey + GetListTab have both settled (success or
    // error) — isLoading is false both when a query resolves and when it's disabled
    // (isLoginLocalStorage falsy, so the tabs query never runs).
    if (isLoadingSystemKeys || isLoadingTabs || isLoadingMenu) return

    dispatch(setAppInitialized(true))
  }, [dispatch, isLoadingMenu, isLoadingSystemKeys, isLoadingTabs])

  // Thông báo realtime qua SignalR; sự kiện lỡ lúc đứt kết nối không được phát lại nên nối lại thì refetch
  useNotificationStream({
    enabled: !!isLogin,
    onNotification: () => {
      queryClient.setQueryData(notificationQueryKeys.hasUnread(), { isError: false, object: true })
      queryClient.invalidateQueries({ queryKey: notificationQueryKeys.all })
    },
    onUnreadCount: (count) => {
      queryClient.setQueryData(notificationQueryKeys.hasUnread(), {
        isError: false,
        object: count > 0,
      })
      queryClient.setQueryData(notificationQueryKeys.unreadCount(), {
        isError: false,
        object: count,
      })
    },
    onReconnect: () => {
      queryClient.invalidateQueries({ queryKey: notificationQueryKeys.all })
    },
    // Thông báo bị thu hồi: tải lại danh sách và số chưa đọc của chuông (cùng gốc query key)
    onRevoked: () => {
      queryClient.invalidateQueries({ queryKey: notificationQueryKeys.all })
    },
  })

  return <>{children}</>
}

export default DefaultAction
